import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  ErrorText,
  StyledLabel,
  StyledOption,
  StyledOptionsList,
  StyledOptionsWrapper,
  StyledSelectButton,
  StyledSelectWrapper,
} from './select.style';
import { IntlMessages } from '../../../helper/langHelper';
import InputField from '../inputField';

const SearchableSelectComponent = ({
  options = [],
  selectedOption = () => {},
  label = '',
  placeholder = 'Select an option',
  sizevalue = 'normal',
  disabled = false,
  error = false,
  errorText = '',
  defaultValue = null,
  id = 'select-component',
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const selectRef = useRef(null);
  const optionsRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(
        (option) =>
          option.procedureName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.procedureName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
      setFocusedIndex(0);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Manage focus for keyboard navigation
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current) {
      const optionElements = optionsRef.current.querySelectorAll('[role="option"]');
      if (optionElements[focusedIndex]) {
        optionElements[focusedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, focusedIndex]);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setFocusedIndex(0);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    setSelected(option);
    selectedOption(option);
    setSearchTerm('');
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex((prevIndex) => (prevIndex + 1) % filteredOptions.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(filteredOptions.length - 1);
        } else {
          setFocusedIndex(
            (prevIndex) => (prevIndex - 1 + filteredOptions.length) % filteredOptions.length
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleSelect(filteredOptions[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      default:
        break;
    }
  };

  // Handle option keyboard events
  const handleOptionKeyDown = (e, option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(option);
    }
  };

  // Display text
  const getDisplayText = () => {
    return selected ? selected.procedureName : placeholder;
  };

  // Generate IDs for accessibility
  const labelId = `${id}-label`;
  const listboxId = `${id}-listbox`;

  return (
    <StyledSelectWrapper ref={selectRef}>
      {label && (
        <StyledLabel id={labelId} htmlFor={id} sizevalue={sizevalue}>
          {label}
        </StyledLabel>
      )}

      <StyledSelectButton
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? `${labelId} ${id}` : id}
        aria-controls={isOpen ? listboxId : undefined}
        onClick={toggleDropdown}
        disabled={disabled}
        isOpen={isOpen}
        sizevalue={sizevalue}
        color={error ? 'error' : undefined}
      >
        <span>{getDisplayText()}</span>
        <ChevronDown size={16} />
      </StyledSelectButton>

      {isOpen && !disabled && (
        <StyledOptionsWrapper>
          <div style={{ padding: '4px', borderBottom: '1px solid #e5e7eb' }}>
            <InputField
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder="Type to search..."
              aria-label="Search options"
              sizevalue="normal"
            />
          </div>
          <StyledOptionsList
            ref={optionsRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
          >
            {loading ? (
              <StyledOption
                role="option"
                tabIndex={-1}
                isSelected={false}
                sizevalue={sizevalue}
                aria-disabled="true"
              >
                <IntlMessages id="options.loading.message" />
              </StyledOption>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <StyledOption
                  key={option.value || option._id}
                  role="option"
                  id={`${id}-option-${index}`}
                  tabIndex={-1}
                  aria-selected={selected?.value === option.value || selected?._id === option._id}
                  onClick={() => handleSelect(option)}
                  onKeyDown={(e) => handleOptionKeyDown(e, option)}
                  isSelected={selected?.value === option.value || selected?._id === option._id}
                  className={focusedIndex === index ? 'focused' : ''}
                  sizevalue={sizevalue}
                >
                  {option.icon && <span aria-hidden="true">{option.icon}</span>}
                  {option.procedureName || option.name}
                </StyledOption>
              ))
            ) : (
              <StyledOption
                role="option"
                tabIndex={-1}
                isSelected={false}
                sizevalue={sizevalue}
                aria-disabled="true"
              >
                <IntlMessages id="options.empty.state" />
              </StyledOption>
            )}
          </StyledOptionsList>
        </StyledOptionsWrapper>
      )}

      {error && errorText && <ErrorText>{errorText}</ErrorText>}
    </StyledSelectWrapper>
  );
};

export default SearchableSelectComponent;
